/** Canonical wire contract, also imported by the PlayCanvas client. No server dependencies. */
export const PROTOCOL_VERSION = 1;
export const SYNC_INTERVAL_MS = 50;
export const LEVEL_NAMES = ["新贵", "明俊", "豪杰", "泰斗", "白帝"] as const;
export const LEVEL_MAX = 50;
/** 每个称号覆盖 10 级：新贵 1–10、明俊 11–20、豪杰 21–30、泰斗 31–40、白帝 41–50。 */
export function levelTitle(level: number): string {
  const clamped = Math.min(Math.max(Math.trunc(level) || 1, 1), LEVEL_MAX);
  return LEVEL_NAMES[Math.min(LEVEL_NAMES.length - 1, Math.floor((clamped - 1) / 10))];
}
export type Vec3 = [number, number, number];
export type Quat = [number, number, number, number];
export type Locomotion = "idle" | "walk" | "run" | "jump" | "fly";
export interface Profile {
  id: number;
  username: string;
  organization: string;
  /** 隐身模式：其他客户端不渲染该玩家（模型 + 名牌）。 */
  hidden: boolean;
  level: number;
  avatarId: string;
  revision: number;
}
export interface Actor extends Profile {
  role: "user" | "host" | "admin";
}
export interface Room {
  id: string;
  name: string;
  sceneId: string;
  hostUserId: number;
  maxParticipants: number;
  passwordHash: string | null;
  status: "open" | "locked" | "closed";
  revision: number;
}
export interface Scene {
  id: string;
  name: string;
  manifest: Record<string, any>;
  revision: number;
  enabled: boolean;
}
export interface Avatar {
  id: string;
  name: string;
  modelUrl: string;
  config: Record<string, any>;
  revision: number;
  enabled: boolean;
}
export interface Portal {
  sourceRoomId: string;
  portalId: string;
  targetRoomId: string;
  targetSpawn: Vec3 | null;
  targetAngles: Vec3 | null;
  enabled: boolean;
  revision: number;
}
export interface InputState {
  membershipEpoch: number;
  poseRevision: number;
  seq: number;
  position: Vec3;
  rotation: Quat;
  velocity: Vec3;
  locomotion: Locomotion;
  grounded: boolean;
}
export interface ActionState {
  actionId: string | null;
  actionSeq: number;
  startedAt: number;
  duration: number;
  loop: boolean;
}
export interface PlayerState extends InputState {
  userId: number;
  sampleTime: number;
  poseRevision: number;
  motionRevision: number;
  motionStartedAt: number;
}
export interface Player {
  profile: Profile;
  state: PlayerState;
  action: ActionState;
  status: "online" | "transferring" | "reconnecting";
  avatar: Avatar;
}
export interface Snapshot {
  serverEpoch: string;
  serverTime: number;
  serverTick: number;
  room: { id: string; name: string; sceneId: string; maxParticipants: number };
  memberRevision: number;
  players: Player[];
}
export interface StateBatch {
  serverEpoch: string;
  serverTime: number;
  serverTick: number;
  roomId: string;
  states: PlayerState[];
}
export interface Admission {
  room: Room;
  scene: Scene;
  avatar: Avatar;
  membershipEpoch: number;
  spawn: Vec3;
  angles: Vec3;
  resumeToken: string;
}
export interface Transfer extends Admission {
  transferId: string;
}
export type Reply<T> =
  | { ok: true; data: T }
  | { ok: false; error: { code: string; message: string } };
export interface RuntimeEvent {
  target: { roomId: string } | { userId: number };
  name: string;
  data: unknown;
  volatile?: boolean;
}
